<script lang="ts">
    import Checkbox from '../../../../ui-components/checkbox/Checkbox.svelte';
    import type {AcceptableRootValueEditorLocalizations} from '../types';
    import {createEventDispatcher, getContext} from 'svelte';
    import type {ValueEditorSize} from '../../../types';
    import type {EditorsLocalizationFunction} from '../../../localizations/types';
    import {EDITORS_LOCALIZE} from '../../../context-keys';
    import type {TreeNode, TreeNodeEventMap, TreeNodeWithChildren, TreeState} from './types';
    import {flattenTree, isAnySubtreeNodeChecked, isNodeChecked, isNodeExpanded, treeNodesEquals} from './utils';

    export let treeNode: TreeNodeWithChildren;
    export let size: ValueEditorSize;
    export let baseId: string;
    export let isRoot = true;
    export let subtreeIndex = 0;

    let nodeElementRef: HTMLElement;

    const dispatch = createEventDispatcher<TreeNodeEventMap>();
    const localize: EditorsLocalizationFunction<AcceptableRootValueEditorLocalizations> = getContext(EDITORS_LOCALIZE);
    const {expandedNodes$, focusedNode$, checkedNodesIds$} = getContext<TreeState<string | number>>('tree-state');

    let isChecked: boolean;
    let isIndeterminate: boolean;

    $: isChecked = isNodeChecked($checkedNodesIds$, treeNode);
    $: isIndeterminate = !isChecked && isAnySubtreeNodeChecked($checkedNodesIds$, treeNode);

    let isExpanded: boolean;
    $: isExpanded = isNodeExpanded($expandedNodes$, treeNode);

    $: {
        if (nodeElementRef) {
            if (!!$focusedNode$ && treeNodesEquals($focusedNode$, treeNode)) {
                nodeElementRef.tabIndex = 0;
                nodeElementRef.focus();
            } else if (isRoot && !$focusedNode$) {
                nodeElementRef.tabIndex = 0;
            } else {
                nodeElementRef.tabIndex = -1;
            }
        }
    }

    function toggleExpand() {
        if (isExpanded) {
            $expandedNodes$ = $expandedNodes$.filter((expandedNode) => !treeNodesEquals(expandedNode, treeNode));
        } else {
            $expandedNodes$ = [...$expandedNodes$, treeNode];
        }
    }

    let hasChildNodes: boolean;
    $: hasChildNodes = treeNode.children && treeNode.children.length > 0;


    function removeDuplicateNodes(nodes: TreeNode[]): TreeNode[] {
        return Array.from(new Set([...nodes]));
    }

    function handleChange(event: CustomEvent<{ checked: boolean }>) {
        toggleNode(event.detail.checked); // new state
    }

    function toggleNode(selected: boolean) {
        const subTreeNodes = flattenTree(treeNode);
        $expandedNodes$ = removeDuplicateNodes([...$expandedNodes$, ...subTreeNodes]);
        if (selected) {
            dispatch('add', {node: treeNode});
        } else {
            dispatch('remove', {node: treeNode});
        }
    }

    const keyCode = Object.freeze({
        RETURN: 'Enter',
        SPACE: 'Space',
        PAGEUP: 'PageUp',
        PAGEDOWN: 'PageDown',
        END: 'End',
        HOME: 'Home',
        LEFT: 'ArrowLeft',
        UP: 'ArrowUp',
        RIGHT: 'ArrowRight',
        DOWN: 'ArrowDown',
    });

    // inspired by implementation in https://www.w3.org/WAI/ARIA/apg/patterns/treeview/
    function handleKeydown(event: KeyboardEvent) {
        let flag = false;

        if (event.altKey || event.ctrlKey || event.metaKey) {
            return;
        }

        switch (event.code) {
            case keyCode.RETURN:
            case keyCode.SPACE:

                if (!treeNode.disabled) {
                    toggleNode(!isChecked); // isChecked is old/current state => negation
                }

                flag = true;
                break;

            case keyCode.UP:
                dispatch('focusPreviousNode', {node: treeNode});
                flag = true;
                break;

            case keyCode.DOWN:
                dispatch('focusNextNode', {node: treeNode});
                flag = true;
                break;

            case keyCode.RIGHT:
                if (hasChildNodes) {
                    if (isExpanded) {
                        dispatch('focusNextNode', {node: treeNode});
                    } else {
                        toggleExpand();
                    }
                }
                flag = true;
                break;

            case keyCode.LEFT:
                if (hasChildNodes && isExpanded) {
                    toggleExpand();
                    flag = true;
                } else {
                    if (!isRoot) {
                        dispatch('focusParentNode', {node: treeNode});
                        flag = true;
                    }
                }
                break;

            case keyCode.HOME:
                dispatch('focusFirstNode', {node: treeNode});
                flag = true;
                break;

            case keyCode.END:
                dispatch('focusLastNode', {node: treeNode});
                flag = true;
                break;
        }

        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

</script>

<template>
    <!-- svelte-ignore a11y-role-has-required-aria-props a11y-interactive-supports-focus WE USE ARIA-CHECKED INSTEAD OF ARIA-SELECTED, AND WE SET TABINDEX MANUALLY -->
    <div bind:this={nodeElementRef}
         class="node-content size-{size}"
         role="treeitem"
         data-root-node={isRoot}
         aria-checked={isChecked}
         aria-disabled={treeNode.disabled}
         {...hasChildNodes ? {'aria-expanded': isExpanded} : {}}
         {...hasChildNodes && isExpanded ? {'aria-owns': `${baseId}-${treeNode.id}-children`} : {}}
         on:keydown={handleKeydown}
         data-subtree-index={subtreeIndex}>
        <span class="glyphicon glyphicon-triangle-right expand-subtree-toggle-btn"
              aria-hidden="true"
              title={isExpanded ? localize('collapseNode') : localize('expandNode')}
              class:expanded={isExpanded}
              class:tree-leaf={!hasChildNodes}
              on:click={toggleExpand}>
        </span>
        <Checkbox id={`${baseId}-checkbox-${treeNode.id}`}
                  checked={isChecked}
                  disabled={treeNode.disabled}
                  indeterminate={isIndeterminate}
                  {size}
                  tabindex="-1"
                  on:change={handleChange}>
            <slot node={treeNode}></slot>
            {#if (treeNode.disabled)}
                {localize('cannotSelect')}
            {/if}
        </Checkbox>
    </div>
    {#if (isExpanded)}
        <ul class="size-{size}" role="group" id={`${baseId}-${treeNode.id}-children`}>
            {#each treeNode?.children ?? [] as childNode, index}
                <li>
                    <svelte:self treeNode={childNode}
                                 {baseId}
                                 {size}
                                 isRoot={false}
                                 subtreeIndex={index}
                                 on:add
                                 on:remove
                                 on:focusPreviousNode
                                 on:focusNextNode
                                 on:focusParentNode
                                 on:focusFirstNode
                                 on:focusLastNode
                                 let:node>
                        <slot {node}></slot>
                    </svelte:self>
                </li>
            {/each}

        </ul>
    {/if}
</template>

<style lang="less">
    .node-content {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        display: flex;
        flex-direction: row;
        align-items: center;

        line-height: 24px;

        .expand-subtree-toggle-btn {
            cursor: pointer;
            height: 16px;
            padding: 0;
            margin-right: 6px; // gap between toggle and checkbox

            &.tree-leaf {
                visibility: hidden;
            }

            &::before {
                display: block;
                transition: transform 0.2s;
            }

            &.expanded::before {
                transform: rotate(90deg);
            }
        }

        &.size-md {
            padding-top: 3px;
            padding-bottom: 3px;
        }

        &.size-sm {
            padding-top: 2px;
            padding-bottom: 2px;
        }

        &.size-xs {
            padding-top: 0;
            padding-bottom: 1px;
        }

        &.size-sm, &.size-xs {
            font-size: 12px;

            .expand-subtree-toggle-btn {
                height: 14px;
            }
        }
    }

    ul {
        list-style: none;
        overflow: hidden;
        margin: 0;
        padding: 0 0 0 20px;

        &.size-sm {
            padding-left: 16px;
        }

        &.size-xs {
            padding-left: 10px;
        }
    }
</style>